/**
 * iter-159 — Trial-signup attribution.
 *
 * Reads a ``?source=<key>`` query param when the user lands on any
 * employer-CTA page and stores it in localStorage. Later, when the
 * employer finally clicks "Start free trial" from the Pricing page or
 * the Trial Banner, ``getTrialSource()`` returns the stored source so
 * ``/api/billing/checkout`` can stamp it on the user doc + Stripe
 * subscription metadata.
 *
 * Why localStorage, not sessionStorage: an employer often lands via
 * the Home CTA, bounces to signup, verifies email, and only picks a
 * plan hours (or days) later. Session storage would be gone by then.
 *
 * Allowlist mirrors ``TRIAL_SOURCES_ALLOWED`` on the backend — any
 * other value is coerced to ``direct`` server-side.
 */

const STORAGE_KEY = "sim_trial_source";
const TTL_MS = 30 * 24 * 3600 * 1000; // 30 days; after that we assume a fresh intent.

export const TRIAL_SOURCES = Object.freeze([
    "direct",
    "home_final_cta",
    "home_hero_cta",
    "pricing_hero",
    "pricing_plan_card",
    "recruit_cta",
    "platform_page",
    "employer_signup",
    "trial_banner",
    "admin",
]);

const ALLOWED = new Set(TRIAL_SOURCES);

/**
 * Call this once on any inbound CTA page to persist the ``?source=``
 * param if present. Safe to call on every mount — only writes when
 * the URL param is a known value AND we don't already have a
 * fresher stamp.
 */
export function captureTrialSourceFromURL() {
    try {
        const params = new URLSearchParams(window.location.search);
        const raw = (params.get("source") || "").toLowerCase().trim();
        if (!raw || !ALLOWED.has(raw)) return;
        const existing = readRaw();
        // Only overwrite if the new source is from a hotter inbound
        // click (we treat every explicit URL hit as hotter than a
        // previously-stored value — attribution leans toward latest-touch).
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            source: raw,
            at: Date.now(),
        }));
        // Strip the ``source`` param from the URL so a refresh doesn't
        // keep re-firing analytics, and a user who shares the link
        // doesn't inadvertently poison the next visitor's attribution.
        params.delete("source");
        const next = params.toString();
        const url = window.location.pathname + (next ? `?${next}` : "") + window.location.hash;
        window.history.replaceState({}, "", url);
        void existing; // (kept for future "first-touch" vs "last-touch" tuning)
    } catch {
        /* ignore quota / disabled storage */
    }
}

function readRaw() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.source || !ALLOWED.has(parsed.source)) return null;
        if (!parsed.at || Date.now() - parsed.at > TTL_MS) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

/** Returns the stored source, falling back to ``"direct"``. */
export function getTrialSource() {
    const stored = readRaw();
    return stored?.source || "direct";
}

/** Clears the stored source — call after a successful checkout. */
export function clearTrialSource() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}
